/**
 * ---------------------------------------------------------------------------------------------------------------
 * BUNDLE SET BUILDER
 * ---------------------------------------------------------------------------------------------------------------
 *
 * The pre-set bundle configurator (snippets/bundle-builder-set.liquid) — the drawer's "Build Your Own" panel
 * when the bundle_buy_buttons block is in Pre-set mode. Redesigned 2026-08-25 to Xavier's mockups: a centred
 * rounded popup whose STAGE (layered deep-etch preview) stays fixed at the top, with a single horizontal strip
 * of square tiles beneath it.
 *
 * The strip has two states, swapped in place:
 *   menu     — one tile per SLOT, showing the current pick's thumbnail ("+" when the optional add-on is off)
 *   options  — after tapping a slot tile: a Back tile, then one tile per colourway of that slot (optional
 *              slots lead with a "None" tile, which IS the off switch — there is no separate toggle)
 * Selecting a colour re-layers the stage live and stays in the options row so colours can be compared;
 * Back returns to the menu. All state lives here; Liquid emits the tiles, layers and seeded defaults.
 *
 * The add-to-cart flow deliberately mirrors BundleBuilder._addItems (same bundled sections, same `variant:add` /
 * `cart:change` events, same Unicorn hand-off) so the cart drawer keeps working and the bundle analytics tap
 * counts these sets without changes.
 *
 * Pricing
 * -------
 * By default the set is the sum of its components at their own prices — lines carry the `_kit` property (the
 * goal-mode convention): the volume-tier discount function only prices `_bundle` lines, so a set can never pick
 * up a ladder discount nobody advertised.
 *
 * COMPLETE-SET DISCOUNT (2026-09-02): when the set's entry in the `bundle.tiers` shop metafield is a rule
 * ({"discount": 10, "requires": 3, "discount_addons": false}), Liquid passes `setDiscount` in the config and
 * flips `bundleProperty` to `_bundle`. Lines are then stamped `_bundle: "set:<key>:<timestamp>"` plus
 * `_slot: <slot id>` ("+"-prefixed on optional add-ons), and the discount function grants the percentage only
 * while all required slots are still in the cart — remove a component and the saving drops. The maths here
 * (per-line rounding) matches the function's, so the number shown is the number charged. Optional add-ons are
 * excluded from the shown/charged saving unless the rule sets discount_addons.
 */

class BundleSet extends HTMLElement {
  connectedCallback() {
    // Same reconnect contract as BundleBuilder: listeners re-attach on every connect (removed in
    // disconnectedCallback; addEventListener with the same reference never duplicates), state builds once.
    this.addEventListener('click', this._onClick);

    if (this._initialized) {
      return;
    }
    this._initialized = true;

    this.config = this._readConfig();
    this.nav = this.querySelector('[data-bbs-nav]');
    this.rows = Array.from(this.querySelectorAll('[data-bbs-row]'));

    /**
     * One entry per slot, built from its menu tile. `chosen` is the selected option tile (its dataset carries
     * variant id, price, thumb); `enabled` is always true for required slots — an optional slot follows its
     * "None" tile instead, and remembers its last colour while off so reopening lands where the shopper left it.
     */
    this.slots = Array.from(this.querySelectorAll('[data-bbs-slot-tile]')).map((tile) => {
      const id = tile.dataset.slot;
      const row = this.rows.find((entry) => entry.dataset.bbsRow === id) || null;
      const options = row ? Array.from(row.querySelectorAll('[data-bbs-option]')) : [];
      const optional = tile.dataset.optional === 'true';
      const enabled = tile.dataset.active === 'true';
      const preset = options.find((option) => option.dataset.selected === 'true' && option.dataset.available === 'true');

      return {
        id,
        tile,
        row,
        options,
        optional,
        enabled,
        thumb: tile.querySelector('[data-bbs-tile-thumb]'),
        plus: tile.querySelector('[data-bbs-tile-plus]'),
        noneTile: row ? row.querySelector('[data-bbs-none]') : null,
        // Only the viewed product's slot arrives chosen (Liquid marks it selected). Everything else starts as
        // a "+" the shopper fills in — no silent defaults (2026-08-25, Xavier).
        chosen: preset || null
      };
    });

    /** 'menu', or the slot id whose options row is showing. */
    this.view = 'menu';

    this.layers = Array.from(this.querySelectorAll('[data-bbs-layer]'));
    this.submitButton = this.querySelector('[data-bbs-submit]');
    this.errorElement = this.querySelector('[data-bbs-error]');
    this.saveElement = this.querySelector('[data-bbs-save]');

    // Edge fades on the tile strip: each row manages its own, from its own scroll position.
    this.rows.forEach((row) => {
      row.addEventListener('scroll', () => this._updateFade(row), { passive: true });
    });

    this._render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _readConfig() {
    const script = this.querySelector('[data-bbs-config]');

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('[bundle-set] invalid config', error);
      return {};
    }
  }

  /* -------------------------------------------------------------------------------------------------------------
     Interaction
  ------------------------------------------------------------------------------------------------------------- */

  _onClick = (event) => {
    const trigger = event.target.closest('[data-bbs-slot-tile], [data-bbs-back], [data-bbs-none], [data-bbs-option], [data-bbs-submit]');

    if (!trigger || !this.contains(trigger)) {
      return;
    }

    event.preventDefault();

    if (trigger.hasAttribute('data-bbs-submit')) {
      this._submit();
      return;
    }

    if (trigger.hasAttribute('data-bbs-slot-tile')) {
      this.view = trigger.dataset.slot;
      this._render();
      return;
    }

    if (trigger.hasAttribute('data-bbs-back')) {
      this.view = 'menu';
      this._render();
      return;
    }

    const slot = this.slots.find((entry) => entry.id === trigger.dataset.slot);

    if (!slot) {
      return;
    }

    if (trigger.hasAttribute('data-bbs-none')) {
      // "None" switches the optional add-on off; the last colour is kept so re-enabling restores it.
      slot.enabled = false;
      this._render();
      return;
    }

    // A colourway tile. Picking one on an optional slot IS opting in.
    if (trigger.dataset.available !== 'true') {
      return;
    }

    slot.chosen = trigger;
    slot.enabled = true;
    this._render();
  };

  /* -------------------------------------------------------------------------------------------------------------
     Selection
  ------------------------------------------------------------------------------------------------------------- */

  /** The slots that will actually ship: enabled with a chosen, available option. */
  get activeSlots() {
    return this.slots.filter((slot) => slot.enabled && slot.chosen);
  }

  /** True when every required slot has a pickable option — an all-sold-out required slot blocks the set. */
  get complete() {
    return this.slots.every((slot) => slot.optional || slot.chosen);
  }

  get subtotal() {
    return this.activeSlots.reduce((total, slot) => total + (parseInt(slot.chosen.dataset.price, 10) || 0), 0);
  }

  /**
   * The complete-set saving in cents — 0 while incomplete or undiscounted. Summing per-line rounded amounts
   * (not rounding the summed percentage once) is deliberate: it is exactly how the discount function computes
   * its fixed-amount path and how Shopify applies a per-line percentage, so the promise matches the charge to
   * the cent.
   */
  get savingCents() {
    const rule = this.config.setDiscount;

    if (!rule || !this.complete) {
      return 0;
    }

    return this.activeSlots.reduce((total, slot) => {
      if (slot.optional && !rule.discountAddons) {
        return total;
      }

      const price = parseInt(slot.chosen.dataset.price, 10) || 0;
      return total + Math.round(price * (rule.percent / 100));
    }, 0);
  }

  /* -------------------------------------------------------------------------------------------------------------
     Rendering
  ------------------------------------------------------------------------------------------------------------- */

  _render() {
    this._renderRows();
    this._renderTiles();
    this._renderStage();
    this._renderSubmit();
    this._setError(null);
  }

  /** Shows the row the current view names, hides the rest. Display flipping restarts the row's CSS entrance. */
  _renderRows() {
    if (this.nav) {
      this.nav.dataset.view = this.view === 'menu' ? 'menu' : 'options';
    }

    this.rows.forEach((row) => {
      row.hidden = row.dataset.bbsRow !== this.view;
    });

    const visible = this.rows.find((row) => !row.hidden);
    if (visible) {
      this._updateFade(visible);
    }
  }

  /**
   * The strip scrolls on its own (never the popup), and fades the edge that has more tiles behind it. The
   * fade is a mask driven by data-fade so it appears only when there is genuinely something to scroll to.
   */
  _updateFade(row) {
    const max = row.scrollWidth - row.clientWidth;

    if (max <= 1) {
      delete row.dataset.fade;
      return;
    }

    const x = row.scrollLeft;
    row.dataset.fade = x <= 1 ? 'right' : x >= max - 1 ? 'left' : 'both';
  }

  _renderTiles() {
    this.slots.forEach((slot) => {
      const showThumb = slot.enabled && slot.chosen && slot.chosen.dataset.thumb;

      slot.tile.dataset.active = slot.enabled && slot.chosen ? 'true' : 'false';

      if (slot.thumb) {
        if (showThumb) {
          // Only touch src when it changes, so the browser never refetches on unrelated re-renders.
          if (slot.thumb.getAttribute('src') !== slot.chosen.dataset.thumb) {
            slot.thumb.src = slot.chosen.dataset.thumb;
          }
          slot.thumb.hidden = false;
        } else {
          slot.thumb.hidden = true;
        }
      }

      if (slot.plus) {
        slot.plus.hidden = Boolean(showThumb);
      }

      slot.options.forEach((option) => {
        const selected = slot.enabled && option === slot.chosen;
        option.dataset.selected = selected ? 'true' : 'false';
        option.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      if (slot.noneTile) {
        const noneSelected = !slot.enabled;
        slot.noneTile.dataset.selected = noneSelected ? 'true' : 'false';
        slot.noneTile.setAttribute('aria-pressed', noneSelected ? 'true' : 'false');
      }
    });
  }

  _renderStage() {
    this.layers.forEach((layer) => {
      const slot = this.slots.find((entry) => entry.id === layer.dataset.slot);
      const active = Boolean(slot && slot.enabled && slot.chosen && slot.chosen.dataset.variantId === layer.dataset.option);
      layer.dataset.active = active ? 'true' : 'false';

      // Non-default layers ship with loading="lazy" and are display-none until picked; flip them eager once
      // they're needed so the swap doesn't wait on an intersection that display:none never triggers.
      if (active && layer.tagName === 'IMG' && layer.loading === 'lazy') {
        layer.loading = 'eager';
      }
    });
  }

  _renderSubmit() {
    if (!this.submitButton) {
      return;
    }

    const labels = this.config.labels || {};
    const label = this.submitButton.querySelector('[data-bbs-submit-label]') || this.submitButton;

    if (!this.complete) {
      this.submitButton.disabled = true;

      // "Choose your Satellite Journal" while picks are missing; "Currently unavailable" only when a required
      // slot genuinely has nothing in stock to pick.
      const missing = this.slots.find((slot) => !slot.optional && !slot.chosen);
      const pickable = missing && missing.options.some((option) => option.dataset.available === 'true');

      if (missing && pickable) {
        label.textContent = this._interpolate(labels.choose || 'Choose your [label]', {
          label: missing.tile.dataset.label || ''
        });
      } else {
        label.textContent = labels.submitUnavailable || '';
      }
    } else {
      this.submitButton.disabled = false;
      // With a complete-set discount the button shows what the shopper will actually pay; the save line above
      // it carries the struck full price.
      label.textContent = this._interpolate(labels.submit, {
        count: this.activeSlots.length,
        total: this._formatMoney(this.subtotal - this.savingCents)
      });
    }

    this._renderSaving();
  }

  /** The save line under the summary: struck full price + save label, only while a discounted set is complete. */
  _renderSaving() {
    if (!this.saveElement) {
      return;
    }

    const saving = this.savingCents;

    if (!saving) {
      this.saveElement.hidden = true;
      return;
    }

    const full = this.saveElement.querySelector('[data-bbs-save-full]');
    const text = this.saveElement.querySelector('[data-bbs-save-text]');

    if (full) {
      full.textContent = this._formatMoney(this.subtotal);
    }

    if (text) {
      text.textContent = this._interpolate((this.config.labels || {}).save || 'Save [amount]', {
        amount: this._formatMoney(saving),
        percent: this.config.setDiscount.percent
      });
    }

    this.saveElement.hidden = false;
  }

  _setError(message) {
    if (!this.errorElement) {
      return;
    }

    this.errorElement.textContent = message || '';
    this.errorElement.dataset.visible = message ? 'true' : 'false';
  }

  /* -------------------------------------------------------------------------------------------------------------
     Add to cart
  ------------------------------------------------------------------------------------------------------------- */

  async _submit() {
    if (!this.complete || this.dataset.busy === 'true') {
      return;
    }

    this.dataset.busy = 'true';
    this.submitButton?.setAttribute('aria-busy', 'true');

    // Same property convention as the BYO builder: one shared value groups the lines into a set, both for the
    // cart and for order attribution. `_kit` (the default) is deliberately NOT the property the volume-discount
    // function prices on. A DISCOUNTED set instead rides `_bundle` with the reserved `set:` prefix, and each
    // line names its slot so the function can insist on the complete set ("+" marks an optional add-on, which
    // never counts toward completeness).
    const rule = this.config.setDiscount;
    const property = this.config.bundleProperty || '_kit';
    const key = this.config.bundleKey || 'set';
    const setId = rule ? `set:${key}:${Date.now()}` : `${key}-${Date.now()}`;

    const items = this.activeSlots.map((slot) => ({
      id: parseInt(slot.chosen.dataset.variantId, 10),
      quantity: 1,
      properties: rule
        ? { [property]: setId, _slot: `${slot.optional ? '+' : ''}${slot.id}` }
        : { [property]: setId }
    }));

    try {
      await this._addItems(items);
    } catch (error) {
      this._setError(error.message || (this.config.labels || {}).genericError);
    } finally {
      this.dataset.busy = 'false';
      this.submitButton?.removeAttribute('aria-busy');
    }
  }

  /** Mirrors BundleBuilder._addItems — see bundle-builder.js for why each step exists. */
  async _addItems(items) {
    const root = window.Shopify.routes.root;
    const sectionsToBundle = ['variant-added'];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest' // needed for Shopify to check inventory
      },
      // sections_url is required: without it Shopify returns no rendered sections and CartDrawer._onCartChanged
      // reads cart["sections"][sectionId] unguarded.
      body: JSON.stringify({
        items,
        sections: sectionsToBundle.join(','),
        sections_url: window.location.pathname
      })
    });

    const responseJson = await response.json();

    if (!response.ok) {
      throw new Error(responseJson.description || responseJson.message);
    }

    if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
      window.location.href = `${root}cart`;
      return;
    }

    const cartContent = await (await fetch(`${root}cart.js`)).json();
    cartContent['sections'] = responseJson['sections'];

    // Same hand-off as BundleBuilder: close the drawer so the cart drawer opening underneath is actually seen.
    this.closest('x-drawer')?.hide();

    this.dispatchEvent(
      new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: responseJson['items'] || [responseJson], cart: cartContent }
      })
    );

    if (cartContent['sections'] && Object.keys(cartContent['sections']).length > 0) {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent }
        })
      );
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    // The theme's own cart drawer is disabled in favour of the Unicorn app, which listens to none of the events
    // above — open it through its own API so the shopper actually SEES their new set.
    try {
      const unicorn = window.Unicorn;

      if (unicorn && typeof unicorn.openCart === 'function') {
        Promise.resolve(typeof unicorn.refreshCart === 'function' ? unicorn.refreshCart() : null).finally(() =>
          unicorn.openCart()
        );
      }
    } catch {
      // the events above remain the fallback
    }
  }

  /* -------------------------------------------------------------------------------------------------------------
     Helpers — same [placeholder] and money-format conventions as bundle-builder.js
  ------------------------------------------------------------------------------------------------------------- */

  _interpolate(template, replacements) {
    if (!template) {
      return '';
    }

    return Object.keys(replacements).reduce(
      (result, key) => result.replaceAll(`[${key}]`, replacements[key]),
      template
    );
  }

  _formatMoney(cents) {
    const format = this.config.moneyFormat || '${{amount}}';
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);

    if (!placeholder) {
      return format;
    }

    const withDelimiters = (precision, thousands, decimal) => {
      const value = (cents / 100.0).toFixed(precision);
      const parts = value.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
    };

    let value;

    switch (placeholder[1]) {
      case 'amount':
        value = withDelimiters(2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = withDelimiters(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(2, "'", '.');
        break;
      default:
        value = withDelimiters(2, ',', '.');
    }

    return format.replace(placeholder[0], value);
  }
}

if (!window.customElements.get('bundle-set')) {
  window.customElements.define('bundle-set', BundleSet);
}
